import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserCheck, FolderKanban, Palette, HardHat, ShoppingCart,
  Receipt, Megaphone, Briefcase, Calculator, Box, AlertCircle, Sparkles, CheckSquare,
  BarChart3, Settings, Search, Bell, LogOut, ChevronsLeft, ChevronsRight, Lock, PencilRuler, FileSpreadsheet, PackageCheck, Headset, UsersRound, Gauge,
} from "lucide-react";
import { IllamAppIcon } from "@/components/IllamLogo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GlobalSearch } from "@/components/GlobalSearch";

const NAV = [
  { id: "control-tower", name: "Control Tower", to: "/control-tower", icon: Gauge, enabled: true, perm: "management.view" },
  { id: "dashboard", name: "Dashboard", to: "/dashboard", icon: LayoutDashboard, enabled: true },
  { id: "crm", name: "CRM Leads", to: "/crm", icon: Users, enabled: true, perm: "leads.view" },
  { id: "customers", name: "Customers", to: "/customers", icon: UserCheck, enabled: true, perm: "customers.view" },
  { id: "projects", name: "Projects", to: "/projects", icon: FolderKanban, enabled: true, perm: "projects.view" },
  { id: "design", name: "Design Studio", to: "/design", icon: Palette, enabled: true, perm: "designs.view" },
  { id: "designer", name: "Designer Workspace", to: "/designer", icon: PencilRuler, enabled: true, perm: "designs.view" },
  { id: "commercial", name: "Commercial Review", to: "/commercial", icon: FileSpreadsheet, enabled: true, perm: "boq.view" },
  { id: "operations", name: "Site Operations", to: "/operations", icon: HardHat, enabled: true, perm: "operations.view" },
  { id: "handover", name: "Handover", to: "/handover", icon: PackageCheck, enabled: true, perm: "handover.view" },
  { id: "service", name: "Customer Service", to: "/service", icon: Headset, enabled: true, perm: "service.view" },
  { id: "workforce", name: "Workforce & Assets", to: "/workforce", icon: UsersRound, enabled: true, perm: "workforce_analytics.view" },
  { id: "procurement", name: "Procurement", to: "/procurement", icon: ShoppingCart, enabled: true, perm: "procurement.view" },
  { id: "finance", name: "Finance", to: "/finance", icon: Receipt, enabled: true, perm: "finances.view" },
  { id: "marketing", name: "Marketing", to: "/marketing", icon: Megaphone, enabled: true, perm: "marketing.view" },
  { id: "hr", name: "HR", to: "/hr", icon: Briefcase, enabled: true, perm: "hr.view" },
  { id: "price_master", name: "Price Master", to: "/price-master", icon: Calculator, enabled: true, perm: "price_master.view" },
  { id: "assets", name: "Assets", to: "/assets", icon: Box, enabled: true, perm: "assets.view" },
  { id: "tickets", name: "Tickets", to: "/tickets", icon: AlertCircle, enabled: true, perm: "tickets.view" },
  { id: "ai", name: "ILLAM AI", to: "/ai", icon: Sparkles, enabled: true, perm: "ai.view" },
  { id: "approvals", name: "Approvals", to: "/approvals", icon: CheckSquare, enabled: true, perm: "approvals.view" },
  { id: "reports", name: "Reports", to: "/reports", icon: BarChart3, enabled: true, perm: "reports.view" },
  { id: "settings", name: "Settings", to: "/settings", icon: Settings, enabled: true, perm: "settings.view" },
];

export function AppShell({ children, title }) {
  const { user, organisation, role, logout, can } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [approvalsCount, setApprovalsCount] = useState(0);

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    if (can("approvals.view"))
      api.get("/approvals", { params: { status: "Pending" } }).then((r) => setApprovalsCount(r.data.length)).catch(() => {});
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  const initials = useMemo(
    () => `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase(),
    [user]
  );

  const visibleNav = NAV.filter((n) => !n.perm || !n.enabled || can(n.perm));

  return (
    <div className="flex h-screen overflow-hidden bg-[#F5F2EB]">
      {/* Sidebar */}
      <aside
        className={`illam-scroll-dark flex flex-col bg-[#3E3E3E] text-[#F5F2EB] transition-[width] duration-200 ${collapsed ? "w-[72px]" : "w-[248px]"}`}
      >
        <div className="flex items-center justify-between px-4 h-[60px] border-b border-[#4A4A4A] shrink-0">
          <IllamAppIcon size={collapsed ? 34 : 40} />
        </div>
        <div className="px-3 py-2 shrink-0">
          <div className={`rounded-md bg-[#333333] border border-[#4f4f4f] px-3 py-2 ${collapsed ? "hidden" : "block"}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#8a8a8a]">Organisation</div>
            <div className="text-sm font-medium truncate">{organisation?.display_name || "ILLAM"}</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto illam-scroll-dark px-2 pb-4 space-y-0.5">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            if (!n.enabled)
              return (
                <div
                  key={n.id}
                  data-testid={`sidebar-nav-${n.id}-disabled`}
                  className="group flex items-center gap-3 rounded-md px-3 py-2 text-sm text-[#6b6b6b] cursor-not-allowed select-none"
                  title="Coming Next"
                >
                  <Icon size={18} className="shrink-0" />
                  {!collapsed && (
                    <span className="flex-1 flex items-center justify-between">
                      {n.name}
                      <span className="text-[9px] uppercase tracking-wider bg-[#2a2a2a] text-[#8a8a8a] px-1.5 py-0.5 rounded">Soon</span>
                    </span>
                  )}
                </div>
              );
            return (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/dashboard"}
                data-testid={`sidebar-nav-${n.id}-link`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-[#F56B1F] text-white" : "text-[#cfcabf] hover:bg-[#333333] hover:text-white"
                  }`
                }
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span>{n.name}</span>}
              </NavLink>
            );
          })}
        </nav>
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-collapse-toggle"
          className="flex items-center gap-2 px-4 h-11 border-t border-[#4A4A4A] text-[#9a9a9a] hover:text-white text-sm transition-colors shrink-0"
        >
          {collapsed ? <ChevronsRight size={18} /> : <><ChevronsLeft size={18} /> Collapse</>}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-[60px] shrink-0 bg-white/90 backdrop-blur-md border-b border-[#E3DAC8] flex items-center gap-3 px-6">
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="topbar-global-search-input"
            className="flex items-center gap-2 text-sm text-neutral-500 bg-[#EEE9DE] hover:bg-[#ece7dd] border border-[#E3DAC8] rounded-md px-3 py-1.5 w-72 transition-colors"
          >
            <Search size={16} />
            <span className="flex-1 text-left">Search projects, customers…</span>
            <kbd className="text-[10px] mono bg-white border border-[#E3DAC8] rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
          <div className="flex-1" />
          <NavLink to="/approvals" data-testid="topbar-approvals-chip" className="relative p-2 rounded-md hover:bg-[#EEE9DE] transition-colors">
            <CheckSquare size={19} className="text-neutral-700" />
            {approvalsCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#F56B1F] text-white text-[9px] mono rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{approvalsCount}</span>
            )}
          </NavLink>
          <button data-testid="topbar-notifications-button" className="relative p-2 rounded-md hover:bg-[#EEE9DE] transition-colors">
            <Bell size={19} className="text-neutral-700" />
            {notifs.unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#F56B1F] text-white text-[9px] mono rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{notifs.unread}</span>
            )}
          </button>
          <div className="w-px h-7 bg-[#E3DAC8]" />
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-[#3E3E3E] text-white flex items-center justify-center text-xs font-semibold" data-testid="topbar-user-avatar-button">
              {initials || "U"}
            </div>
            <div className="hidden md:block leading-tight">
              <div className="text-sm font-medium text-neutral-900">{user?.first_name} {user?.last_name}</div>
              <div className="text-[11px] text-neutral-500">{role?.name || "—"}</div>
            </div>
            <button onClick={logout} data-testid="topbar-logout-button" title="Sign out" className="p-2 rounded-md hover:bg-[#EEE9DE] transition-colors">
              <LogOut size={18} className="text-neutral-600" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {title && <h1 className="font-heading text-2xl font-bold tracking-tight text-neutral-900 mb-5" data-testid="page-title">{title}</h1>}
          <div className="animate-fade-up">{children}</div>
        </main>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} onNavigate={(t) => { navigate(t); setSearchOpen(false); }} />
    </div>
  );
}

export function ComingSoon({ name }) {
  return (
    <AppShell title={name}>
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="coming-soon">
        <div className="h-16 w-16 rounded-2xl bg-[#EEE9DE] border border-[#E3DAC8] flex items-center justify-center mb-5">
          <Lock className="text-[#F56B1F]" size={26} />
        </div>
        <h2 className="font-heading text-lg font-semibold text-neutral-900">{name} — Coming Next</h2>
        <p className="text-sm text-neutral-500 max-w-md mt-2">
          This module ships in a later ILLAM build. The foundation (permissions, events, approvals, AI) is already in place so it can plug in without rework.
        </p>
      </div>
    </AppShell>
  );
}
